/**
 * Hook de validation temps réel pour la feuille d'heures.
 * Extrait de useTimesheetActions pour réduire sa taille et isoler les règles métier.
 */
import { useMemo, useCallback } from 'react';
import { dateKey } from '../utils/dateUtils';
import type { Project } from '../types';
import type {
  GridRow,
  DailyIndemnity,
  Ventilation,
  ValidationIssue,
  ValidationResult,
} from '../pages/timesheet/types';

const MAX_DAILY_HOURS = 12;

interface UseTimesheetValidationParams {
  rows: GridRow[];
  indemnities: Record<string, DailyIndemnity>;
  ventilations: Record<string, Ventilation>;
  weekDays: Date[];
  holidays: string[];
  trainingDays: string[];
  projects: Project[];
  isBE: boolean;
  plannedGDDays: Set<string>;
  /** Optionnel : ignore les erreurs BE sur les jours d'un autre mois (semaine à cheval). */
  isOutsideEntryMonth?: (day: Date) => boolean;
  /** Optionnel : ignore les erreurs BE sur les jours déjà soumis/validés. */
  isLockedForDay?: (day: Date) => boolean;
}

export function useTimesheetValidation(params: UseTimesheetValidationParams) {
  const {
    rows, indemnities, ventilations,
    weekDays, holidays, trainingDays,
    projects, isBE, plannedGDDays,
    isOutsideEntryMonth,
    isLockedForDay,
  } = params;

  const validateTimesheet = useCallback((): ValidationResult => {
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];

    // Calcul des heures totales et par jour
    const dailyHours: Record<string, number> = {};
    let totalWeekHours = trainingDays.length * 7;

    weekDays.forEach(day => {
      const dKey = dateKey(day);
      dailyHours[dKey] = 0;

      if (trainingDays.includes(dKey)) {
        dailyHours[dKey] = 7;
        return;
      }

      rows.forEach(row => {
        const h = row.hours[dKey] || 0;
        dailyHours[dKey] += h;
        totalWeekHours += h;
      });
    });

    // ===== RÈGLE 1: Heures journalières max =====
    weekDays.forEach(day => {
      const dKey = dateKey(day);
      const hours = dailyHours[dKey] || 0;

      if (hours > MAX_DAILY_HOURS) {
        errors.push({
          severity: 'error',
          message: `${day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} : ${hours}h dépasse le maximum autorisé (${MAX_DAILY_HOURS}h)`,
          field: `hours_${dKey}`,
          day: dKey
        });
      }
    });

    // ===== RÈGLE 2: Heures sur jours fériés =====
    weekDays.forEach(day => {
      const dKey = dateKey(day);
      if (holidays.includes(dKey) && dailyHours[dKey] > 0) {
        warnings.push({
          severity: 'warning',
          message: `${day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} est férié - ${dailyHours[dKey]}h saisies`,
          field: `hours_${dKey}`,
          day: dKey
        });
      }
    });

    // ===== RÈGLE 3: Cohérence heures/indemnités =====
    weekDays.forEach(day => {
      const dKey = dateKey(day);
      const hours = dailyHours[dKey] || 0;
      const ind = indemnities[dKey];
      const dayOfWeek = day.getDay();

      if ((dayOfWeek === 0 || dayOfWeek === 6) && hours === 0) {
        if (ind?.meals === 1 || ind?.transport > 0 || ind?.trajet > 0) {
          warnings.push({
            severity: 'warning',
            message: `${day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} : indemnités saisies sans heures de travail`,
            field: `indemnity_${dKey}`,
            day: dKey
          });
        }
      }

      if (hours === 0 && ind?.meals === 1 && !trainingDays.includes(dKey) && !holidays.includes(dKey)) {
        warnings.push({
          severity: 'warning',
          message: `${day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} : panier repas sans heures saisies`,
          field: `indemnity_${dKey}`,
          day: dKey
        });
      }
    });

    // ===== RÈGLE 4: Ventilation BE obligatoire =====
    // Skip les jours outside entry month (semaine à cheval : employé verra
    // les erreurs quand il passe sur le mois concerné) et les jours locked
    // (déjà submitted/validated, plus modifiables). Cohérent avec le guard
    // dans useTimesheetActions.saveToDatabase.
    if (isBE) {
      rows.forEach(row => {
        Object.entries(row.hours).forEach(([dKey, h]) => {
          if (h > 0) {
            const dayDate = new Date(dKey);
            if (isOutsideEntryMonth && isOutsideEntryMonth(dayDate)) return;
            if (isLockedForDay && isLockedForDay(dayDate)) return;

            const ventKey = `${row.projectId}_${dKey}`;
            const vent = ventilations[ventKey];
            const totalVent = vent ? (vent.etude + vent.suivi + vent.mise_en_service + vent.chantier) : 0;

            if (Math.abs(totalVent - h) > 0.01) {
              errors.push({
                severity: 'error',
                message: `${row.projectName} le ${new Date(dKey).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} : ventilation incomplète (${totalVent}h / ${h}h)`,
                field: `ventilation_${row.projectId}_${dKey}`,
                day: dKey
              });
            }
          }
        });
      });
    }

    // ===== RÈGLE 5: GD sans projet GD =====
    weekDays.forEach(day => {
      const dKey = dateKey(day);
      const ind = indemnities[dKey];

      if (ind?.is_grand_deplacement) {
        const hasGDProject = rows.some(row => {
          const proj = projects.find(p => p.id === row.projectId);
          return proj?.zone === 'GD' && (row.hours[dKey] || 0) > 0;
        });

        if (!hasGDProject) {
          warnings.push({
            severity: 'warning',
            message: `${day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} : GD activé mais aucun chantier en zone GD`,
            field: `gd_${dKey}`,
            day: dKey
          });
        }
      }
    });

    // ===== RÈGLE 6: Aucune heure saisie =====
    if (totalWeekHours === 0) {
      errors.push({
        severity: 'error',
        message: 'Aucune heure saisie cette semaine',
        field: 'total_hours'
      });
    }

    // ===== RÈGLE 5b: GD vs planning AdminScheduler =====
    if (plannedGDDays.size > 0) {
      weekDays.forEach(day => {
        const dKey = dateKey(day);
        const ind = indemnities[dKey];
        const isGDChecked = ind?.is_grand_deplacement === true;
        const isPlannedGD = plannedGDDays.has(dKey);
        const hours = dailyHours[dKey] || 0;
        const dayLabel = day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

        if (isGDChecked && !isPlannedGD) {
          warnings.push({
            severity: 'warning',
            message: `${dayLabel} : coché Grand Déplacement mais ne figure pas dans le planning`,
            field: `gd_planning_${dKey}`,
            day: dKey
          });
        }

        if (isPlannedGD && !isGDChecked && hours > 0) {
          warnings.push({
            severity: 'warning',
            message: `${dayLabel} : planifié sur un chantier GD mais non coché Grand Déplacement`,
            field: `gd_planning_${dKey}`,
            day: dKey
          });
        }
      });
    }

    return { isValid: errors.length === 0, errors, warnings };
  }, [rows, indemnities, weekDays, trainingDays, holidays, projects, ventilations, isBE, plannedGDDays]);

  const validationResult = useMemo(() => validateTimesheet(), [validateTimesheet]);

  return { validationResult };
}
